import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  CircularProgress,
  Container
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Receipt,
  Visibility,
  CheckCircle,
  Cancel,
  Warning,
} from "@mui/icons-material";
import { returnAPI } from "../../services/API";
import { useSnackbar } from "notistack";

export const Returns = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReturns();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await returnAPI.getAll(params);
      const { saleReturns: returnsData, pagination: paginationData } =
        response.data.data;

      setReturns(returnsData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching returns", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, returnItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedReturn(returnItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReturn(null);
  };

  const handleApprove = async () => {
    try {
      await returnAPI.approve(selectedReturn._id);
      enqueueSnackbar("Return approved successfully", { variant: "success" });
      setApproveDialog(false);
      fetchReturns();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error approving return",
        { variant: "error" }
      );
    }
    handleMenuClose();
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      enqueueSnackbar("Please provide a rejection reason", {
        variant: "warning",
      });
      return;
    }

    try {
      await returnAPI.reject(selectedReturn._id, { rejectionReason });
      enqueueSnackbar("Return rejected successfully", { variant: "success" });
      setRejectDialog(false);
      setRejectionReason("");
      fetchReturns();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error rejecting return",
        { variant: "error" }
      );
    }
    handleMenuClose();
  };

  const handleComplete = async () => {
    try {
      await returnAPI.complete(selectedReturn._id);
      enqueueSnackbar("Return completed successfully", { variant: "success" });
      fetchReturns();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error completing return",
        { variant: "error" }
      );
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      approved: "info",
      completed: "success",
      rejected: "error",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Warning sx={{ fontSize: '1.1rem' }} />,
      approved: <CheckCircle sx={{ fontSize: '1.1rem' }} />,
      completed: <CheckCircle sx={{ fontSize: '1.1rem' }} />,
      rejected: <Cancel sx={{ fontSize: '1.1rem' }} />,
      cancelled: <Cancel sx={{ fontSize: '1.1rem' }} />,
    };
    return icons[status];
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Returns Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add sx={{ fontSize: '1.3rem' }} />} 
            onClick={() => navigate("/hospital/pharmacy/returns/new")} 
            sx={{ 
              borderRadius: 2,
              fontSize: '1.1rem',
              py: 1.5,
              px: 3
            }}
          >
            New Return
          </Button>
        </Box>

        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  variant="outlined" 
                  placeholder="Search by return number, patient name, or invoice number." 
                  value={searchTerm} 
                  onChange={(e) => handleSearch(e.target.value)} 
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: '1.3rem' }} />
                      </InputAdornment>
                    ),
                    style: { fontSize: '1.1rem' }
                  }} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ fontSize: '1.2rem' }}>
                  Total: {pagination.total} returns
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: "Pending Approval", value: returns.filter((r) => r.status === "pending").length, color: "warning.main" },
            { label: "Approved", value: returns.filter((r) => r.status === "approved").length, color: "info.main" },
            { label: "Completed", value: returns.filter((r) => r.status === "completed").length, color: "success.main" },
            { label: "Rejected", value: returns.filter((r) => r.status === "rejected").length, color: "error.main" },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center", "&:hover": { boxShadow: 6, transform: "translateY(-4px)" } }}>
                <CardContent>
                  <Typography variant="h2" fontWeight="bold" sx={{ color: c.color, fontSize: '3rem' }}>
                    {c.value}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                    {c.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
              Recent Returns
            </Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Return #</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Invoice #</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Patient</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Refund Amount</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Refund Method</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returns.map((returnItem) => (
                        <TableRow key={returnItem._id} hover sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                          <TableCell>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              {returnItem.returnNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {returnItem.saleInvoice?.invoiceNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {returnItem.patient && (
                              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                {returnItem.patient.firstName} {returnItem.patient.lastName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {new Date(returnItem.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                              ₹{returnItem.totalRefund}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              icon={getStatusIcon(returnItem.status)} 
                              label={returnItem.status.toUpperCase()} 
                              color={getStatusColor(returnItem.status)} 
                              size="medium"
                              sx={{ 
                                fontSize: '0.9rem',
                                '& .MuiChip-label': { fontSize: '0.9rem' }
                              }} 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={returnItem.refundMethod?.replace("_", " ")} 
                              variant="outlined" 
                              size="medium"
                              sx={{ 
                                fontSize: '0.9rem',
                                '& .MuiChip-label': { fontSize: '0.9rem' }
                              }} 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="large" 
                              onClick={(e) => handleMenuOpen(e, returnItem)}
                              sx={{ fontSize: '1.3rem' }}
                            >
                              <MoreVert sx={{ fontSize: '1.5rem' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {returns.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Receipt sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontSize: '1.5rem' }}>
                      No returns found
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
                      {searchTerm ? "Try adjusting your search terms" : "Get started by creating your first return"}
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Add sx={{ fontSize: '1.3rem' }} />} 
                      onClick={() => navigate("/hospital/pharmacy/returns/new")}
                      sx={{ 
                        fontSize: '1.1rem',
                        py: 1.5,
                        px: 3
                      }}
                    >
                      Create First Return
                    </Button>
                  </Box>
                )}

                {pagination.pages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <Pagination 
                      count={pagination.pages} 
                      page={pagination.page} 
                      onChange={handlePageChange} 
                      color="primary" 
                      showFirstButton 
                      showLastButton 
                      size="large"
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Menu 
          anchorEl={anchorEl} 
          open={Boolean(anchorEl)} 
          onClose={handleMenuClose}
          PaperProps={{ sx: { '& .MuiMenuItem-root': { fontSize: '1.1rem' } } }}
        >
          <MenuItem onClick={() => navigate(`/hospital/pharmacy/returns/${selectedReturn?._id}`)}>
            <Visibility sx={{ mr: 1, fontSize: '1.3rem' }} /> 
            View Details
          </MenuItem>
          {selectedReturn?.status === "pending" && [
            <MenuItem key="approve" onClick={() => setApproveDialog(true)}>
              <CheckCircle sx={{ mr: 1, fontSize: '1.3rem' }} color="success" /> 
              Approve Return
            </MenuItem>, 
            <MenuItem key="reject" onClick={() => setRejectDialog(true)}>
              <Cancel sx={{ mr: 1, fontSize: '1.3rem' }} color="error" /> 
              Reject Return
            </MenuItem>
          ]}
          {selectedReturn?.status === "approved" && (
            <MenuItem onClick={handleComplete}>
              <CheckCircle sx={{ mr: 1, fontSize: '1.3rem' }} color="success" /> 
              Complete Return
            </MenuItem>
          )}
        </Menu>

        {/* Approve / Reject dialogs */}
        <Dialog open={approveDialog} onClose={() => setApproveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.3rem' }}>Approve Return</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '1.1rem' }}>
              Are you sure you want to approve return <strong>{selectedReturn?.returnNumber}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialog(false)} sx={{ fontSize: '1.1rem' }}>
              Cancel
            </Button>
            <Button onClick={handleApprove} variant="contained" sx={{ fontSize: '1.1rem' }}>
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.3rem' }}>Reject Return</DialogTitle>
          <DialogContent>
            <TextField 
              fullWidth 
              multiline 
              rows={4} 
              label="Rejection Reason" 
              value={rejectionReason} 
              onChange={(e) => setRejectionReason(e.target.value)}
              InputProps={{ style: { fontSize: '1.1rem' } }}
              InputLabelProps={{ style: { fontSize: '1.1rem' } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialog(false)} sx={{ fontSize: '1.1rem' }}>
              Cancel
            </Button>
            <Button onClick={handleReject} variant="contained" color="error" sx={{ fontSize: '1.1rem' }}>
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};